/**
 * @module customerDataApiService
 * @description Service for handling customer data API requests.
 */

import Axios from 'axios'
import { getHeadersWithJwtToken } from '../auth/utils/auth-util'
import { apiRoutes } from '../constants/routes'
import {
	ICustomerInfo,
	ICustomer,
	IFilteredLoans,
} from '../interfaces/i-api-loan'
import { CookieType } from '../constants/cookieTypes'
import { getCustomerInfoCookieName, waitForLanguage } from '../utils/helpers'
import { TFunction } from 'i18next'
import { SigningUrlType } from '../constants/signingTypes'
import { CountryCode } from '../constants/countryTypes'
import appConfig from '../app.config'
import { IKycData } from '../interfaces/i-api-kyc'
import { IDeposit, IDepositCustomerData } from '../interfaces/i-api-deposit'
import {
	IAddPublicNote,
	IApplicationInformation,
	IApplicationUrlsDto,
	IPpiData,
	ISblData,
	IUpdateBankInfo,
	IUpdateCustomerConsents,
	IUpdateMissingKyc,
} from '../interfaces/i-api-application'
import { IFaqEntry } from '../interfaces/i-api-faq'
import { ProductType } from '../interfaces/i-api-support'
import { DocumentTypes } from 'constants/documentTypes'
import { logError } from '../AppInsight'

const transportWithCredentials = Axios.create({})
const isCornicheCountry = ['se', 'fi'].includes(appConfig.country)

transportWithCredentials.interceptors.request.use(async (config) => {
	config.headers['Accept-Language'] = await waitForLanguage()
	return config
})
transportWithCredentials.defaults.headers.common['Instance'] =
	appConfig.mypagesInstance

/**
 * @constant {Object} customerDataApiService
 * @description Object containing methods for customer data API requests.
 */

const customerDataApiService = {
	/**
	 * @function getLoanData
	 * @description Retrieves loan data for a specific country.
	 * @param {string} countryCode - The country code to retrieve loan data for.
	 * @param {TFunction} t - The translation function.
	 * @returns {Promise<IFilteredLoans[]>} A promise that resolves to an array of filtered loans.
	 * @throws {Error} If there is an error retrieving the loan data.
	 */

	async getLoanData(countryCode: string, t: TFunction) {
		try {
			sessionStorage.removeItem(CookieType.LoanCustomer)
			sessionStorage.removeItem(CookieType.CustomerInfo)
			let response = await transportWithCredentials
				.get<ICustomerInfo>(
					apiRoutes.getLoansData(countryCode),
					await getHeadersWithJwtToken(),
				)
				.then((result) => {
					if (result.data.customer) {
						sessionStorage.setItem(
							CookieType.CustomerInfo,
							JSON.stringify(result.data.customer),
						)
						sessionStorage.setItem(
							CookieType.CustomerName,
							JSON.stringify(
								result.data.customer.personalInfo.firstName,
							),
						)
						sessionStorage.setItem(CookieType.LoanCustomer, 'true')
						return result.data.loans as IFilteredLoans[]
					}
				})
				.catch((err) => {
					logError(err)
					if (err.response) {
						if (err.response.status === 404)
							return [] as IFilteredLoans[]
						throw new Error(t('LoanInfoError'))
					} else {
						console.log(err)
						throw new Error(t('LoanInfoError'))
					}
				})
			return response
		} catch (err: any) {
			logError(err)
			throw err
		}
	},

	/**
	 * @function getDepositsData
	 * @description Retrieves deposit data for the customer.
	 * @param {TFunction} t - The translation function.
	 * @returns {Promise<IDeposit[]>} A promise that resolves to an array of deposits.
	 * @throws {Error} If there is an error retrieving the deposit data.
	 */
	async getDepositsData(t: TFunction) {
		try {
			sessionStorage.removeItem(CookieType.DepositCustomer)
			sessionStorage.removeItem(CookieType.DepositCustomerInfo)
			const data = await transportWithCredentials
				.get<IDepositCustomerData>(
					isCornicheCountry
						? apiRoutes.getDepositsDataV2
						: apiRoutes.getDepositsData,
					await getHeadersWithJwtToken(),
				)
				.then((response) => {
					if (response.data.customer) {
						sessionStorage.setItem(
							CookieType.DepositCustomerInfo,
							JSON.stringify(response.data.customer),
						)
						sessionStorage.setItem(
							CookieType.CustomerName,
							JSON.stringify(
								response.data.customer.personalInfo.firstName,
							),
						)
					}
					sessionStorage.setItem(CookieType.DepositCustomer, 'true')
					return response.data.depositAccounts as IDeposit[]
				})
				.catch((err) => {
					logError(err)
					if (err.response) {
						if (err.response.status === 404) return [] as IDeposit[]
						throw new Error(t('DepositInfoError'))
					} else {
						console.log(err)
						throw new Error(t('DepositInfoError'))
					}
				})
			return data
		} catch (err: any) {
			logError(err)
			console.log(err)
			throw err
		}
	},

	/**
	 * @function getCustomerDetails
	 * @description fetch userData.
	 * @throws {Error} If no user is found.
	 */

	async getCustomerDetails() {
		try {
			const data = await transportWithCredentials.get<ICustomer>(
				isCornicheCountry
					? apiRoutes.getCustomerDetailsV2
					: apiRoutes.getCustomerDetails,
				await getHeadersWithJwtToken(),
			)
			if (data) {
				sessionStorage.setItem(
					getCustomerInfoCookieName(),
					JSON.stringify(data),
				)
			}
		} catch (err: any) {
			logError(err)
			console.log(err)
			throw err
		}
	},

	/**
	 * @function updateCustomerDetails
	 * @description Updates customer details.
	 * @param {ICustomer | undefined} userData - The customer data to update.
	 * @param {TFunction} t - The translation function.
	 * @returns {Promise<any>} A promise that resolves when the customer details are updated.
	 * @throws {Error} If there is an error updating the customer details.
	 */

	async updateCustomerDetails(userData: ICustomer | undefined, t: TFunction) {
		try {
			let data
			if (sessionStorage.getItem(CookieType.DepositCustomer)) {
				let depositData = await transportWithCredentials.post(
					isCornicheCountry
						? apiRoutes.updateCustomerDataV2
						: apiRoutes.updateCustomerData,
					userData,
					await getHeadersWithJwtToken(),
				)

				if (depositData.status === 200) {
					sessionStorage.setItem(
						getCustomerInfoCookieName(),
						JSON.stringify(userData),
					)
					data = depositData
				}
			}

			if (sessionStorage.getItem(CookieType.LoanCustomer)) {
				let loanData = await transportWithCredentials.post(
					apiRoutes.updateSubscription,
					userData,
					await getHeadersWithJwtToken(),
				)

				if (loanData.status === 200) {
					sessionStorage.setItem(
						getCustomerInfoCookieName(),
						JSON.stringify(userData),
					)
					//it is ok to override deposit data as we want to use loan data if customer have both loans and deposits
					data = loanData
				}
			}

			if (data) return data

			throw new Error(t('UpdatingCustomerInfoError'))
		} catch (e: any) {
			logError(e)
			console.log(e)
			throw new Error(t('UpdatingCustomerInfoError'))
		}
	},

	/**
	 * @function getSignedDocument
	 * @description Retrieves a signed document.
	 * @param {string} repaymentPlanId - The ID of the repayment plan.
	 * @param {TFunction} t - The translation function.
	 * @returns {Promise<Blob>} A promise that resolves to the signed document.
	 * @throws {Error} If there is an error retrieving the signed document.
	 */

	async getSignedDocument(repaymentPlanId: string, t: TFunction) {
		console.log('ID:' + repaymentPlanId)
		const url = apiRoutes.getRepaymentPlan(repaymentPlanId)
		try {
			const data = await transportWithCredentials
				.get<any>(url, {
					...(await getHeadersWithJwtToken()),
					responseType: 'blob',
				})
				.then((response) => {
					// Success 🎉
					console.log(response)
					return response.data
				})
				.catch((error) => {
					// Error 😨
					if (error.response) {
						/*
						 * The request was made and the server responded with a
						 * status code that falls out of the range of 2xx
						 */
						console.log(error.response.data)
						console.log(error.response.status)
						console.log(error.response.headers)
					} else if (error.request) {
						/*
						 * The request was made but no response was received, `error.request`
						 * is an instance of XMLHttpRequest in the browser and an instance
						 * of http.ClientRequest in Node.js
						 */
						console.log(error.request)
					} else {
						// Something happened in setting up the request and triggered an Error
						console.log('Error', error.message)
					}
					console.log(error.config)

					throw new Error(t('DownloadingFileError'))
				})

			return data
		} catch (e: any) {
			logError(e)
			console.log(e)
			throw new Error(t('DownloadingFileError'))
		}
	},

	/**
	 * @function getDocument
	 * @description Retrieves a document by its ID and type.
	 * @param {string} id - The ID of the document.
	 * @param {DocumentTypes} documentType - The type of the document.
	 * @param {TFunction} t - The translation function.
	 * @returns {Promise<{ blob: Blob, mimeType: string }>} A promise that resolves to the document and its MIME type.
	 * @throws {Error} If there is an error retrieving the document.
	 * @usage Not in use
	 */

	async getDocument(id: string, documentType: DocumentTypes, t: TFunction) {
		try {
			const response = await transportWithCredentials
				.get<any>(apiRoutes.getDocument(id, documentType), {
					...(await getHeadersWithJwtToken()),
					responseType: 'blob',
				})
				.then((response) => ({
					blob: response.data as Blob,
					mimeType: response.headers['x-contenttype'] as string,
				}))
				.catch((error) => {
					console.log(error.config)
					throw new Error(t('DownloadingFileError'))
				})
			return response
		} catch (e: any) {
			logError(e)
			console.log(e)
			throw new Error(t('DownloadingFileError'))
		}
	},

	/**
	 * @function GetApplicationInformation
	 * @description Retrieves application information by application ID.
	 * @param {number} applicationId - The ID of the application.
	 * @param {TFunction} t - The translation function.
	 * @returns {Promise<IApplicationInformation>} A promise that resolves to the application information.
	 * @throws {Error} If there is an error retrieving the application information.
	 */

	async GetApplicationInformation(applicationId: number, t: TFunction) {
		try {
			const response =
				await transportWithCredentials.get<IApplicationInformation>(
					apiRoutes.getApplicationInformation(applicationId),
					await getHeadersWithJwtToken(),
				)
			if (response.status === 200) {
				if (response.data) {
					//----for testing purpose---
					// response.data.lifeCycleStatus = LifeCycleStatus.Disbursed
					// response.data.signingUrls.pnSigning.status = Status.Signed
					// response.data.signingUrls.psD2.status = Status.Created
					// response.data.signingUrls.psD2.isMandatory = true
					//-----test end-----

					return response.data
				}
			}
			throw new Error(t('ApplicationInformationError'))
		} catch (error: any) {
			logError(error)
			console.log(error)
			if (error.response.status === 404)
				throw new Error(t('NoApplicationInformationError'))
			throw new Error(t('ApplicationInformationError'))
		}
	},

	/**
	 * @function GetSigningUrls
	 * @description Retrieves signing URLs for an application.
	 * @param {number} applicationId - The ID of the application.
	 * @param {SigningUrlType} signingUrl - The type of signing URL.
	 * @param {TFunction} t - The translation function.
	 * @returns {Promise<IApplicationUrlsDto>} A promise that resolves to the signing URLs.
	 * @throws {Error} If there is an error retrieving the signing URLs.
	 */

	async GetSigningUrls(
		applicationId: number,
		signingUrl: SigningUrlType,
		t: TFunction,
	) {
		try {
			const response =
				await transportWithCredentials.get<IApplicationUrlsDto>(
					apiRoutes.getSigningUrl(applicationId, signingUrl),
					await getHeadersWithJwtToken(),
				)

			if (response.status === 200) {
				if (response.data) return response.data
				else throw new Error(t('NoSigningUrlsError'))
			}

			throw new Error(t('SigningUrlsError'))
		} catch (error: any) {
			logError(error)
			console.log(error)
			throw error
		}
	},

	/**
	 * @function UploadDocument
	 * @description Uploads a document for an application.
	 * @param {FormData} uploadDocumentRequest - The document data to upload.
	 * @param {number} applicationId - The ID of the application.
	 * @param {TFunction} t - The translation function.
	 * @throws {Error} If there is an error uploading the document.
	 */

	async UploadDocument(
		uploadDocumentRequest: FormData,
		applicationId: number,
		t: TFunction,
	) {
		console.log(uploadDocumentRequest)
		let auth = await getHeadersWithJwtToken()

		let headers = {
			headers: {
				Authorization: auth.headers.Authorization,
				'Content-Type': 'application/json',
			},
		}
		const response = await transportWithCredentials.post(
			apiRoutes.uploadDocument(applicationId),
			uploadDocumentRequest,
			headers,
		)
		if (response.status !== 200) {
			throw new Error(t('UploadDocumentError'))
		}
	},

	/**
	 * @function getDownloadDocuments
	 * @description Retrieves downloadable documents for an application.
	 * @param {string} applicationId - The ID of the application.
	 * @param {TFunction} t - The translation function.
	 * @returns {Promise<any[]>} A promise that resolves to an array of documents.
	 * @throws {Error} If there is an error retrieving the documents.
	 */

	async getDownloadDocuments(applicationId: string, t: TFunction) {
		try {
			let response = await transportWithCredentials
				.get<any>(
					apiRoutes.getDownloadDocuments(applicationId),
					await getHeadersWithJwtToken(),
				)
				.then((response) => {
					return response.data.documents
				})
				.catch((error) => {
					if (error.response) {
						console.log(error.response.data)
						console.log(error.response.status)
						console.log(error.response.headers)
					} else if (error.request) {
						console.log(error.request)
					} else {
						console.log('Error', error.message)
					}
					console.log(error.config)

					throw new Error(t('DownloadingFileError'))
				})
			return response
		} catch (e: any) {
			logError(e)
			console.log(e)
			throw new Error(t('DownloadingFileError'))
		}
	},

	/**
	 * @function getDownloadDocumentById
	 * @description Retrieves a document by its ID.
	 * @param {string} documentId - The ID of the document.
	 * @returns {Promise<{ blob: Blob }>} A promise that resolves to the document.
	 * @throws {Error} If there is an error retrieving the document.
	 */

	async getDownloadDocumentById(documentId: string) {
		try {
			const response = await transportWithCredentials
				.get<any>(apiRoutes.getDownloadDocumentById(documentId), {
					...(await getHeadersWithJwtToken()),
					responseType: 'blob',
				})
				.then((response) => ({
					blob: response.data as Blob,
				}))
				.catch((error) => {
					console.log(error.config)
					throw new Error(error)
				})
			return response
		} catch (e: any) {
			logError(e)
			console.log(e)
			throw new Error(e)
		}
	},

	/**
	 * @function getKycData
	 * @description Retrieves KYC (Know Your Customer) data.
	 * @param {TFunction} t - The translation function.
	 * @returns {Promise<IKycData>} A promise that resolves to the KYC data.
	 * @throws {Error} If there is an error retrieving the KYC data.
	 * @usage Not in use
	 */

	async getKycData(t: TFunction) {
		try {
			const data = await Axios.get<IKycData>(
				apiRoutes.getKycData,
				await getHeadersWithJwtToken(),
			)
				.then((response) => {
					if (response.status === 200) {
						if (
							sessionStorage.getItem(CookieType.KycRenewal) ==
							null
						) {
							sessionStorage.setItem(
								CookieType.KycRenewal,
								JSON.stringify(response.data.timeToRenew),
							)
						}
					}
					return response.data as IKycData
				})
				.catch((error) => {
					if (error.response) {
						sessionStorage.setItem(
							CookieType.KycRenewal,
							JSON.stringify(false),
						)
						return undefined as unknown as IKycData
					} else {
						logError(error)
						console.log(error)
						throw new Error(t('KycError'))
					}
				})
			return data as IKycData
		} catch (err: any) {
			logError(err)
			throw err
		}
	},

	/**
	 * @function getKycDataWithNationalId
	 * @description Retrieves KYC (Know Your Customer) data using a national ID.
	 * @param {TFunction} t - The translation function.
	 * @param {string} nationalId - The national ID to retrieve KYC data for.
	 * @returns {Promise<IKycData>} A promise that resolves to the KYC data.
	 * @throws {Error} If there is an error retrieving the KYC data.
	 */

	async getKycDataWithNationalId(t: TFunction, nationalId: string) {
		try {
			const data = await Axios.get<IKycData>(
				`${apiRoutes.getKycData}/${nationalId}`,
				await getHeadersWithJwtToken(),
			)
				.then((response) => {
					if (response.status === 200) {
						if (
							sessionStorage.getItem(CookieType.KycRenewal) ==
							null
						) {
							sessionStorage.setItem(
								CookieType.KycRenewal,
								JSON.stringify(response.data.timeToRenew),
							)
						}
					}
					return response.data as IKycData
				})
				.catch((error) => {
					if (error.response) {
						sessionStorage.setItem(
							CookieType.KycRenewal,
							JSON.stringify(false),
						)
						return undefined as unknown as IKycData
					} else {
						logError(error)
						console.log(error)
						throw new Error(t('KycError'))
					}
				})
			return data as IKycData
		} catch (err: any) {
			logError(err)
			throw err
		}
	},

	/**
	 * @function updateKycCustomerData
	 * @description Updates KYC (Know Your Customer) data for a customer.
	 * @param {IKycData} customerData - The KYC data to update.
	 * @returns {Promise<void>} A promise that resolves when the KYC data is updated.
	 * @throws {Error} If there is an error updating the KYC data.
	 * @usage Not in use
	 */

	async updateKycCustomerData(customerData: IKycData) {
		try {
			const data = await Axios.post(
				apiRoutes.updateKycCustomerData,
				customerData,
				await getHeadersWithJwtToken(),
			)
			if (data.status === 200) {
				sessionStorage.setItem(CookieType.KycRenewal, 'false')
				return
			}
			throw new Error(
				`Something went wrong when trying to update kyc info, please try again later`,
			)
		} catch (error: any) {
			logError(error)
			throw error
		}
	},

	/**
	 * @function updatePpi
	 * @description Updates PPI (Payment Protection Insurance) data.
	 * @param {IPpiData} ppiData - The PPI data to update.
	 * @param {TFunction} t - The translation function.
	 * @returns {Promise<number | void>} A promise that resolves to the status code if successful.
	 * @throws {Error} If there is an error updating the PPI data.
	 * @usage Not in use
	 */

	async updatePpi(ppiData: IPpiData, t: TFunction) {
		//if ppi false don´t update px -> unless country is DK
		if (!ppiData.ppi && appConfig.country !== 'dk') return
		try {
			const data = await transportWithCredentials.post(
				apiRoutes.updatePpi,
				ppiData,
				await getHeadersWithJwtToken(),
			)
			if (data.status === 200) return data.status
			throw new Error(t('PpiError'))
		} catch (error) {
			console.log(error)
			throw new Error(t('PpiError'))
		}
	},

	/**
	 * @function updateSbl
	 * @description Updates SBL (Small Business Loan) data.
	 * @param {ISblData} sblData - The SBL data to update.
	 * @param {TFunction} t - The translation function.
	 * @returns {Promise<void>} A promise that resolves when the SBL data is updated.
	 * @throws {Error} If there is an error updating the SBL data.
	 * @usage Not in use
	 */

	async updateSbl(sblData: ISblData, t: TFunction) {
		try {
			const data = await transportWithCredentials.post(
				apiRoutes.updateSbl,
				sblData,
				await getHeadersWithJwtToken(),
			)
			if (data.status === 200) return
			throw new Error(t('SblError'))
		} catch (error) {
			console.log(error)
			throw new Error(t('SblError'))
		}
	},

	/**
	 * @function updateMissingKyc
	 * @description Updates missing KYC (Know Your Customer) data.
	 * @param {IUpdateMissingKyc} updateMissingKycData - The missing KYC data to update.
	 * @param {TFunction} t - The translation function.
	 * @returns {Promise<void>} A promise that resolves when the missing KYC data is updated.
	 * @throws {Error} If there is an error updating the missing KYC data.
	 * @usage Not in use
	 */

	async updateMissingKyc(
		updateMissingKycData: IUpdateMissingKyc,
		t: TFunction,
	) {
		if (!updateMissingKycData) return
		try {
			const data = await transportWithCredentials.post(
				apiRoutes.updateMissingKyc,
				updateMissingKycData,
				await getHeadersWithJwtToken(),
			)
			if (data.status === 200) return
			throw new Error(t('UpdatingMissingKycError'))
		} catch (error) {
			throw new Error(t('UpdatingMissingKycError'))
		}
	},

	/**
	 * @function addPublicNote
	 * @description Adds a public note.
	 * @param {IAddPublicNote} addPublicNoteData - The data for the public note.
	 * @param {TFunction} t - The translation function.
	 * @returns {Promise<void>} A promise that resolves when the public note is added.
	 * @throws {Error} If there is an error adding the public note.
	 * @usage Not in use
	 */

	async addPublicNote(addPublicNoteData: IAddPublicNote, t: TFunction) {
		if (!addPublicNoteData) return
		try {
			const data = await transportWithCredentials.post(
				apiRoutes.addPublicNote,
				addPublicNoteData,
				await getHeadersWithJwtToken(),
			)
			if (data.status === 200) return
			throw new Error(t('UpdatingMissingKycError'))
		} catch (error) {
			throw new Error(t('UpdatingMissingKycError'))
		}
	},

	/**
	 * @function getFAQ
	 * @description Retrieves FAQ entries for a specific country, product type, and language.
	 * @param {string} country - The country code.
	 * @param {ProductType} productType - The product type.
	 * @param {string} lng - The language code.
	 * @param {TFunction} t - The translation function.
	 * @returns {Promise<IFaqEntry[]>} A promise that resolves to an array of FAQ entries.
	 * @throws {Error} If there is an error retrieving the FAQ entries.
	 * @usage Not in use
	 */

	async getFAQ(
		country: string,
		productType: ProductType,
		lng: string,
		t: TFunction,
	) {
		try {
			const data = await Axios.get<IFaqEntry[]>(
				apiRoutes.getFAQ(country, productType, lng),
				await getHeadersWithJwtToken(),
			)
			if (data.status === 200) {
				if (data.data.length !== 0) return data.data as IFaqEntry[]
				else throw new Error(t('FaqNoLng'))
			}
		} catch (error: any) {
			throw new Error(error.message)
		}
	},

	/**
	 * @function updateBankInfo
	 * @description Updates bank information.
	 * @param {IUpdateBankInfo} bankInfo - The bank information to update.
	 * @param {TFunction} t - The translation function.
	 * @returns {Promise<number>} A promise that resolves to the status code if successful.
	 * @throws {Error} If there is an error updating the bank information.
	 * @usage Not in use
	 */

	async updateBankInfo(bankInfo: IUpdateBankInfo, t: TFunction) {
		try {
			let data = await transportWithCredentials.post(
				apiRoutes.updateBankInfo,
				bankInfo,
				await getHeadersWithJwtToken(),
			)

			if (data.status === 200) {
				return data.status
			}
			throw new Error(t('ErrorMsg'))
		} catch (e: any) {
			logError(e)
			console.log(e)
			throw new Error(e.message)
		}
	},

	/**
	 * @function updateCustomerConsent
	 * @description Updates customer consents.
	 * @param {IUpdateCustomerConsents} customerConsents - The customer consents to update.
	 * @param {TFunction} t - The translation function.
	 * @returns {Promise<number>} A promise that resolves to the status code if successful.
	 * @throws {Error} If there is an error updating the customer consents.
	 * @usage Not in use
	 */

	async updateCustomerConsent(
		customerConsents: IUpdateCustomerConsents,
		t: TFunction,
	) {
		try {
			let data = await transportWithCredentials.post(
				apiRoutes.updateCustomerConsents,
				customerConsents,
				await getHeadersWithJwtToken(),
			)

			if (data.status === 200) {
				return data.status
			}
			throw new Error(t('ErrorMsg'))
		} catch (e: any) {
			logError(e)
			console.log(e)
			throw new Error(e.message)
		}
	},

	/**
	 * @function lookupAddressInAddressService
	 * @description Find Addresses .
	 * @param {inputAddress} inputText - the input text.
	 * @returns {Promise<string[]>} A promise that resolves to the array of addresses.
	 * @throws {Error} If there is an error retrieving the address array.
	 * @usage Not in use
	 */

	async lookupAddressInAddressService(inputAddress: string) {
		try {
			const data = await transportWithCredentials.post(
				apiRoutes.lookupAddressInAddressService(inputAddress),
				await getHeadersWithJwtToken(),
			)
			if (data.status === 200) {
				return data.data
			}
		} catch (error) {
			console.log(error)
		}
	},

	/**
	 * @function printPn
	 * @description Prints a PN (Promissory Note) for an application.
	 * @param {number} applicationId - The ID of the application.
	 * @param {TFunction} t - The translation function.
	 * @returns {Promise<number>} A promise that resolves to the status code if successful.
	 * @throws {Error} If there is an error printing the PN.
	 * @usage Not in use
	 */

	async printPn(applicationId: number, t: TFunction) {
		try {
			const data = await transportWithCredentials.post(
				apiRoutes.printPn,
				{ applicationId: applicationId },
				await getHeadersWithJwtToken(),
			)

			if (data.status === 200) return data.status
			throw new Error(t('ErrorMsg'))
		} catch (error) {
			console.log(error)
			throw new Error(t('ErrorMsg'))
		}
	},

	/**
	 * @function addTermsAndConditionAccepted
	 * @description Adds a record indicating that terms and conditions have been accepted.
	 * @returns {Promise<void>} A promise that resolves when the record is added.
	 * @throws {Error} If there is an error adding the record.
	 */

	async addTermsAndConditionAccepted() {
		const headers = await getHeadersWithJwtToken()
		const market =
			CountryCode[appConfig.country as keyof typeof CountryCode]

		try {
			await transportWithCredentials.post(
				apiRoutes.addTermsAndConditionAccepted,
				{ ssn: headers.headers['Customer-Id'], market },
				headers,
			)
		} catch (error: any) {
			throw new Error(error)
		}
	},
}

export default customerDataApiService